import * as React from 'react';
import {Helmet} from 'react-helmet';
import {useDispatch, useSelector} from 'react-redux';
import {Route, Link as RouterLink, Redirect, Switch, withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import mixpanel from 'mixpanel-browser';

import {Session} from 'nutshell-core/session';

import {headTitle} from 'shells/document/head-title';
import {Link} from 'shells/link/link';
import {SecondarySidebarLoading} from 'shells/navigation/secondary-sidebar/secondary-sidebar-loading';

import {setBodyId} from '../../dom';
import type {NutshellState} from '../../store';

import {fetchSchemaAction} from '../forms/forms-actions';
import {PageTitleBar} from '../layout';
import {PageWrapper} from '../layout/page-wrapper';
import {TrashPage} from '../trash/trash-page';

import {mountUserAiFeatures} from './ai-features/user/mount-user-ai-features';
import {PortalledCalendarSettings} from './calendar/portalled-calendar-settings';
import {mountDirectorySync} from './directory-sync/mount-directory-sync';
import {EmailSendingPage} from './email/email-sending/email-sending-page';
import {EmailSyncingPage} from './email/email-syncing/email-syncing-page';
import {mountEmailSignature} from './email/mount-email-signature';
import {EmailDigestsConfigPage} from './email-digests/email-digests-config-page';
import {getSetupSidebar} from './helpers';
import {mountLicensing} from './licensing/mount-licensing';
import {mountMfa} from './mfa/mount-mfa';
import {PhoneSettingsPage} from './phone/phone-settings-page';
import {SetupPage} from './setup-page';
import {PortalledTasksSettings} from './tasks/portalled-tasks-settings';

import 'less/integration.less';
import 'less/my-account.less';
import 'less/setup-activity-benchmark.less';
import 'less/setup-billing.less';
import 'less/setup-builder.less';
import 'less/setup-distribution.less';
import 'less/setup-import.less';
import 'less/setup-index.less';
import 'less/setup-integration.less';
import 'less/setup-process-builder.less';
import 'less/setup-quota.less';
import 'less/setup-region.less';
import 'less/setup-sales-automation.less';
import 'less/setup-segmented-arrows.less';
import 'less/setup-sender-address.less';
import 'less/setup-stage.less';
import 'less/setup-territories.less';
import 'less/setup-unbounce.less';
import 'less/setup.less';
import 'less/filepicker-modal.less';

export type ServerTitleBarProps = {
    pageTitle?: string;
    pageSubtitle?: string;
};

function getSidebarElement(pathname: string, isLoading: boolean): React.ReactElement {
    const bodyElem = document.querySelector('body');
    const isPaused = Boolean(bodyElem && bodyElem.dataset.isInstancePaused);
    const isDisabled = Boolean(bodyElem && bodyElem.dataset.isInstanceDisabled);

    if (isLoading) {
        return <SecondarySidebarLoading />;
    }

    // @ts-expect-error
    return getSetupSidebar(pathname, isPaused, isDisabled) || <></>;
}

export function MyAccountRootComponent(props: ContextRouter) {
    const pathname = props.location.pathname;
    const titles = getPageTitles(pathname);
    const dispatch = useDispatch();
    // @ts-expect-error
    const fetchFormSchema = () => dispatch(fetchSchemaAction());

    const user = useSelector((state: NutshellState) => Session.getSessionStateUser(state));

    React.useEffect(() => {
        fetchFormSchema();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PageWrapper
            header={
                titles ? (
                    <PageTitleBar
                        title={titles.pageTitle}
                        subheaderPrimaryText={
                            titles.pageSubTitle || 'Only admins can view this page'
                        }
                        buttons={titles.buttons}
                    />
                ) : null
            }
            sidebar={getSidebarElement(pathname, !user)}
        >
            <Switch>
                <Route
                    path='/my-account/settings'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('myaccount');

                        // Prevent mixpanel recording on sensitive pages
                        mixpanel.stop_session_recording();

                        return (
                            <SetupPage
                                pageTitle='My Account'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                onViewRendered={(id?: string | null) => {
                                    if (!user) {
                                        return;
                                    }

                                    mountLicensing(user.id);
                                    mountUserAiFeatures(user.id);
                                    mountMfa(id);
                                    mountDirectorySync(user.id);
                                }}
                            />
                        );
                    }}
                />
                <Route
                    path='/my-account/phone'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('myaccount');

                        return (
                            <>
                                <Helmet>
                                    <title>{headTitle('Phone settings')}</title>
                                </Helmet>
                                <PhoneSettingsPage />
                            </>
                        );
                    }}
                />
                <Route
                    path='/my-account/calendar'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('myaccount');

                        return (
                            <SetupPage
                                pageTitle='Calendar Settings'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                portalledContent={PortalledCalendarSettings}
                            />
                        );
                    }}
                />
                <Route
                    path='/my-account/notifications'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('myaccount');

                        return (
                            <SetupPage
                                pageTitle='Notification Settings'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                            />
                        );
                    }}
                />
                <Route
                    path='/my-account/email-digests'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('myaccount');

                        return (
                            <>
                                <Helmet>
                                    <title>{headTitle('Email Digests Settings')}</title>
                                </Helmet>
                                <EmailDigestsConfigPage />
                            </>
                        );
                    }}
                />
                <Route
                    path='/my-account/tasks'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('myaccount');

                        return (
                            <SetupPage
                                pageTitle='Tasks'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                portalledContent={PortalledTasksSettings}
                            />
                        );
                    }}
                />
                <Route
                    path='/my-account/email-sending'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('');

                        return (
                            <>
                                <Helmet>
                                    <title>{headTitle('Email Sending Settings')}</title>
                                </Helmet>
                                <EmailSendingPage />
                            </>
                        );
                    }}
                />
                <Route
                    path='/my-account/email-syncing'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('');

                        return (
                            <>
                                <Helmet>
                                    <title>{headTitle('Email Syncing Settings')}</title>
                                </Helmet>
                                <EmailSyncingPage />
                            </>
                        );
                    }}
                />
                <Route
                    path='/my-account/email-settings'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('myaccount');

                        return (
                            <SetupPage
                                pageTitle='Email Settings'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                onViewRendered={mountEmailSignature}
                            />
                        );
                    }}
                />
                <Route
                    path='/my-account/email-templates'
                    render={() => {
                        return <Redirect to='/email/templates/yours' />;
                    }}
                />
                <Route
                    path='/trash'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('trash');

                        if (user && user.permissions.canDeleteEntities) {
                            return (
                                <>
                                    <Helmet>
                                        <title>{headTitle('Trash')}</title>
                                    </Helmet>
                                    <TrashPage />
                                </>
                            );
                        }

                        return <Redirect to='/dashboard' />;
                    }}
                />
            </Switch>
        </PageWrapper>
    );
}

const baseBodyClasses = ['manage', 'page', 'settings'];

function setBodyClasses(pathname: string): void {
    const body = document.querySelector('body');

    if (!body) return;

    if (pathname.includes('/my-account/settings')) {
        body.className = baseBodyClasses.concat('account-settings').join(' ');
    } else if (pathname.includes('/my-account/phone')) {
        body.className = baseBodyClasses.concat('fixed-width', 'account-phone').join(' ');
    } else if (pathname.includes('/my-account/calendar')) {
        body.className = baseBodyClasses.concat('fixed-width', 'account-calendar').join(' ');
    } else if (pathname.includes('/my-account/tasks')) {
        body.className = baseBodyClasses.concat('fixed-width', 'account-tasks').join(' ');
    } else if (pathname.includes('/my-account/notifications')) {
        body.className = baseBodyClasses.concat('fixed-width', 'account-notifications').join(' ');
    } else if (pathname.includes('/my-account/email-settings')) {
        body.className = baseBodyClasses.concat('fixed-width', 'account-email-settings').join(' ');
    }
}

function getPageTitles(pathname: string):
    | {
          pageTitle: string;
          pageSubTitle: string;
          buttons?: React.ReactNode;
      }
    | null
    | undefined {
    if (pathname.includes('/my-account/email-settings')) {
        return {
            pageTitle: 'Email settings',
            pageSubTitle: 'Sync your email with Nutshell.',
            buttons: (
                // @ts-expect-error
                <Link as={RouterLink} variant='button-secondary' size='big' to='/email/inbox'>
                    Inbox
                </Link>
            ),
        };
    }

    if (pathname.includes('/my-account')) {
        return null;
    }

    if (pathname.includes('/trash')) {
        return null;
    }

    return {
        pageTitle: 'Settings',
        pageSubTitle: '',
    };
}

// @ts-expect-error
export const MyAccountRoot = withRouter<Record<any, any>>(MyAccountRootComponent);
